import { supabase } from '@/integrations/supabase/client';
import { RoadmapTemplate, Step } from '@/types/roadmap';
import { normalizeAttachments } from '@/utils/promptFieldAttachments';

// Raw types representing the database shape returned by Supabase
interface RawLink {
  label: string;
  url: string;
}

interface RawPromptField {
  id: string;
  label: string;
  placeholder?: string;
  tag: string;
  position?: number;
  value?: unknown;
  attachments?: unknown;
  lesson_library_id?: string;
}

interface RawPromptModule {
  id: string;
  title: string;
  prompt_fields: RawPromptField[];
}

interface RawStep {
  id: string;
  title: string;
  description?: string;
  is_open_default?: boolean;
  resources?: string;
  iframe_src?: string;
  download_link?: string;
  download_text?: string;
  position: number;
  links: RawLink[];
  prompt_modules: RawPromptModule[];
}

interface RawSidebarTool {
  id: string;
  label: string;
  url: string;
  description?: string;
  position: number;
}

interface RawSidebarActivity {
  id: string;
  title: string;
  description?: string;
  icon_placeholder?: string;
  href?: string;
  position: number;
}

interface RawSidebarExample {
  id: string;
  text: string;
  href?: string;
  description?: string;
  position: number;
}

interface RawSidebar {
  id: string;
  sidebar_tools?: RawSidebarTool[];
  sidebar_activities?: RawSidebarActivity[];
  sidebar_examples?: RawSidebarExample[];
}

interface RawTemplate {
  id: string;
  title: string;
  description?: string;
  page_title: string;
  embed_url: string;
  embed_title: string;
  tags: string[];
  access_type: string;
  is_public: boolean;
  is_archived?: boolean;
  created_at: string;
  steps: RawStep[];
  sidebars?: RawSidebar[];
}

/**
 * Fetches roadmap templates available to the current user.  The query relies on
 * Postgres Row Level Security policies to scope results to the appropriate
 * company or groups, so no additional client-side filtering is required.
 */
export async function fetchTemplates(): Promise<RoadmapTemplate[]> {
  try {
    const { data, error } = await supabase
      .from('roadmap_templates')
      .select(
        `
        id,
        title,
        description,
        page_title,
        embed_url,
        embed_title,
        tags,
        access_type,
        is_public,
        is_archived,
        created_at,
        steps (
          id,
          title,
          description,
          is_open_default,
          resources,
          iframe_src,
          download_link,
          download_text,
          position,
          links (label, url),
          prompt_modules (
            id,
            title,
            prompt_fields (
              id,
              label,
              placeholder,
              tag,
              position,
              value,
              lesson_library_id,
              attachments
            )
          )
        ),
        sidebars (
          id,
          sidebar_tools (
            id,
            label,
            url,
            description,
            position
          ),
          sidebar_activities (
            id,
            title,
            description,
            icon_placeholder,
            href,
            position
          ),
          sidebar_examples (
            id,
            text,
            href,
            description,
            position
          )
        )
      `
      )
      .or('is_archived.is.null,is_archived.eq.false')
      .order('title');

    if (error) {
      console.error('Failed to fetch templates:', error);
      throw error;
    }

    if (!data) return [];

    // Convert snake_case fields from the database into camelCase structures
    return data.map((t: RawTemplate): RoadmapTemplate => {
      // Process sidebar data (similar to getTemplate.ts)
      const sidebarData = t.sidebars?.[0]; // Templates should have at most one sidebar
      const processedSidebar = sidebarData ? {
        usefulTools: (sidebarData.sidebar_tools || [])
          .sort((a: RawSidebarTool, b: RawSidebarTool) => a.position - b.position)
          .map((tool: RawSidebarTool) => ({
            label: tool.label,
            url: tool.url,
            description: tool.description
          })),
        bonusActivities: (sidebarData.sidebar_activities || [])
          .sort((a: RawSidebarActivity, b: RawSidebarActivity) => a.position - b.position)
          .map((activity: RawSidebarActivity) => ({
            id: activity.id,
            title: activity.title,
            description: activity.description,
            iconPlaceholder: activity.icon_placeholder,
            href: activity.href
          })),
        examples: (sidebarData.sidebar_examples || [])
          .sort((a: RawSidebarExample, b: RawSidebarExample) => a.position - b.position)
          .map((example: RawSidebarExample) => ({
            text: example.text,
            href: example.href,
            description: example.description
          }))
      } : { usefulTools: [], bonusActivities: [], examples: [] };

      return {
        id: t.id,
        title: t.title,
        description: t.description ?? '',
        pageTitle: t.page_title,
        embedUrl: t.embed_url,
        embedTitle: t.embed_title,
        tags: t.tags || [],
        accessType: (t.access_type as RoadmapTemplate['accessType']) || 'public',
        isPublic: t.is_public ?? false,
        is_archived: t.is_archived ?? false,
        createdAt: t.created_at,
        sidebar: processedSidebar,
        steps: (t.steps || []).map((s: RawStep): Step => ({
          id: s.id,
          title: s.title,
          description: s.description ?? '',
          isOpenDefault: s.is_open_default,
          resources: s.resources,
          iframeSrc: s.iframe_src,
          downloadLink: s.download_link,
          downloadText: s.download_text,
          position: s.position,
          links: (s.links || []).map(l => ({ label: l.label, url: l.url })),
          promptModules: (s.prompt_modules || []).map(pm => ({
            id: pm.id,
            title: pm.title,
            promptFields: (pm.prompt_fields || []).map(pf => ({
              id: pf.id,
              label: pf.label,
              placeholder: pf.placeholder,
              tag: pf.tag as any,
              position: pf.position,
              value: pf.value as any,
              lesson_library_id: pf.lesson_library_id,
              attachments: normalizeAttachments(pf.attachments)
            }))
          }))
        }))
      };
    });
  } catch (err) {
    console.error('Failed to fetch templates:', err);
    throw err;
  }
}

/**
 * Admin: Fetch ALL roadmap templates including archived (bypasses RLS)
 */
export async function fetchAllTemplatesAdmin(): Promise<RoadmapTemplate[]> {
  try {
    const { data, error } = await supabase.rpc('get_all_roadmaps_admin');

    if (error) {
      console.error('Failed to fetch admin templates:', error);
      throw error;
    }

    if (!data) return [];

    // Convert to RoadmapTemplate format (simplified - just basic fields)
    return data.map((t: any) => ({
      id: t.id,
      title: t.title,
      description: t.description ?? '',
      pageTitle: t.page_title,
      embedUrl: t.embed_url,
      embedTitle: t.embed_title,
      tags: t.tags || [],
      accessType: t.access_type || 'public',
      isPublic: t.is_public ?? false,
      is_archived: t.is_archived ?? false,
      steps: []  // Admin list doesn't need full step data
    })) as RoadmapTemplate[];
  } catch (err) {
    console.error('Failed to fetch admin templates:', err);
    throw err;
  }
}

/**
 * Admin: Toggle roadmap archive status
 */
export async function toggleRoadmapArchive(roadmapId: string, archive: boolean): Promise<void> {
  const { error } = await supabase.rpc('toggle_roadmap_archive', {
    roadmap_id: roadmapId,
    archive_status: archive
  });

  if (error) {
    console.error('Error toggling roadmap archive:', error);
    throw error;
  }
}
